import { InvestmentAccountType } from '@egw/api'
import { Alert, AlertType, Button, ModalDrawer } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon as InfoIconOutline } from '@heroicons/react/24/outline'
import { useState, type FC } from 'react'
import { buildBasicCurrency, Currency } from '@egw/utils'
import { useTransferContextProvider } from '../../context'

export const IraInfoBlock: FC = () => {
  const { t } = useTranslation('features-transfer')
  const { fromFieldOption, toFieldOption } = useTransferContextProvider()
  const [showModal, setShowModal] = useState<boolean>(false)

  // Show IRA Withdrawal Info Block when From Field is ROTH_IRA or TRADITIONAL_IRA
  // Note if above accounts are selected - only HYC would be shown as To Field
  if (
    fromFieldOption.accountType === InvestmentAccountType.ROTH_IRA ||
    fromFieldOption.accountType === InvestmentAccountType.TRADITIONAL_IRA
  ) {
    return (
      <Alert
        data-testid="ira-info-withdrawal-alert"
        alertType={AlertType.Info}
        showIcon
        title={t('ira.contact_wealth_advisor')}
        description={t('ira.ira_withdrawals')}
      />
    )
  }

  // Show IRA Contribution Info Block when To Field is ROTH_IRA or TRADITIONAL_IRA
  // Note only HYC would be shown as From Field
  if (
    toFieldOption.accountType === InvestmentAccountType.ROTH_IRA ||
    toFieldOption.accountType === InvestmentAccountType.TRADITIONAL_IRA
  ) {
    return (
      <>
        <div data-testid="ira-info-block-wrapper" className="flex flex-col gap-sm">
          {toFieldOption.retirementContribution?.remainingAnnualLimit !== undefined ? (
            <div className="flex flex-row gap-3xs">
              <span className="typo-primary-xs text-text-meta">{t('ira.remaining_contribution')}</span>
              <span className="typo-primary-xs-semibold text-text-body">
                {buildBasicCurrency({ amount: toFieldOption.retirementContribution.remainingAnnualLimit })}
              </span>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => {
              setShowModal(true)
            }}
            data-testid="ira-contribution-limit-popover"
            className="text-text-action typo-primary-base flex flex-row gap-2xs"
          >
            {t('ira.how_much_contribution')}
            <InfoIconOutline className="flex-shrink-0 w-md h-md text-text-action block" />
          </button>
          <Alert
            data-testid="ira-info-deposit-alert"
            alertType={AlertType.Info}
            showIcon
            title={t('ira.contact_wealth_advisor')}
            description={t('ira.ira_deposits')}
          />
        </div>
        <ModalDrawer
          onClose={() => {
            setShowModal(false)
          }}
          title={t('ira.ira_contribution_limits')}
          isOpen={showModal}
        >
          <div data-testid="ira-contribution-limit-popover" className="flex flex-col">
            <h1 className="text-text-body typo-primary-lg-semibold">{t('ira.contribution_modal_heading')}</h1>
            <ul className="typo-primary-sm-regular text-text-body list-disc list-inside mt-xs">
              <li>{t('ira.contribution_li_1')}</li>
              <li>{t('ira.contribution_li_2')}</li>
              <li>{t('ira.contribution_li_3')}</li>
            </ul>
            <p className="typo-primary-sm-regular text-text-body mt-md">{t('ira.contribution_p_1')}</p>
            <p className="typo-primary-sm-regular text-text-body mt-md">{t('ira.contribution_p_2')}</p>
            <Button
              data-testid="close-ira-contribution-limit-popover"
              onClick={() => {
                setShowModal(false)
              }}
              className="mt-md"
            >
              {t('ira.close')}
            </Button>
          </div>
        </ModalDrawer>
      </>
    )
  }
}
